import React from 'react';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

// Color palette untuk charts
const COLORS = {
  valid: '#10b981',      // green-500
  warning: '#f59e0b',    // amber-500
  critical: '#ef4444',   // red-500
  expired: '#6b7280',    // gray-500
};

// Mini Sparkline Chart untuk Stats Card
export function MiniSparkline({ data, color = '#3b82f6', height = 40 }) {
  if (!data || data.length === 0) return null;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data}>
        <Line
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={2}
          dot={false}
          animationDuration={1000}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

// Certificate Status Distribution Pie Chart
export function StatusPieChart({ data }) {
  if (!data || data.length === 0) return null;

  const chartData = [
    { name: 'Valid', value: data.valid || 0, color: COLORS.valid },
    { name: 'Akan Kedaluwarsa', value: data.warning || 0, color: COLORS.warning },
    { name: 'Kritis', value: data.critical || 0, color: COLORS.critical },
    { name: 'Kedaluwarsa', value: data.expired || 0, color: COLORS.expired },
  ].filter(item => item.value > 0);

  return (
    <Card className="h-full flex flex-col min-h-[400px]">
      <CardHeader className="flex-shrink-0">
        <CardTitle className="text-base sm:text-lg">Distribusi Status</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col justify-center">
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={5}
              dataKey="value"
              animationDuration={1000}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '8px 12px'
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="grid grid-cols-2 gap-2 mt-4">
          {chartData.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">{entry.name}:</span>
              <span className="font-semibold">{entry.value}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Certificate Expiry Timeline Chart
export function ExpiryTimelineChart({ certificates }) {
  if (!certificates || certificates.length === 0) return null;

  // Group certificates by expiry timeframe
  const now = new Date();
  const timeframes = [
    { label: '0-7 hari', days: 7, count: 0 },
    { label: '8-30 hari', days: 30, count: 0 },
    { label: '31-60 hari', days: 60, count: 0 },
    { label: '61-90 hari', days: 90, count: 0 },
    { label: '>90 hari', days: 999, count: 0 },
  ];

  certificates.forEach(cert => {
    const expiryDate = new Date(cert.expiration_date);
    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry <= 7) timeframes[0].count++;
    else if (daysUntilExpiry <= 30) timeframes[1].count++;
    else if (daysUntilExpiry <= 60) timeframes[2].count++;
    else if (daysUntilExpiry <= 90) timeframes[3].count++;
    else timeframes[4].count++;
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base sm:text-lg">Timeline Kedaluwarsa</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {timeframes.map((timeframe, index) => {
            const percentage = certificates.length > 0
              ? (timeframe.count / certificates.length) * 100
              : 0;

            const barColor =
              timeframe.days <= 7 ? 'bg-red-500' :
                timeframe.days <= 30 ? 'bg-orange-500' :
                  timeframe.days <= 60 ? 'bg-yellow-500' :
                    'bg-green-500';

            return (
              <div key={index}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-muted-foreground">{timeframe.label}</span>
                  <span className="font-semibold">{timeframe.count} sertifikat</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full ${barColor} transition-all duration-1000 ease-out`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// Trend Chart untuk Overview
export function CertificateTrendChart({ data }) {
  if (!data || data.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base sm:text-lg">Trend Sertifikat</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data}>
            <Line
              type="monotone"
              dataKey="total"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ fill: '#3b82f6', r: 4 }}
              animationDuration={1500}
            />
            <Line
              type="monotone"
              dataKey="expiring"
              stroke="#f59e0b"
              strokeWidth={2}
              dot={{ fill: '#f59e0b', r: 4 }}
              animationDuration={1500}
            />
            <Tooltip
              contentStyle={{
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '8px 12px'
              }}
            />
          </LineChart>
        </ResponsiveContainer>
        <div className="flex items-center justify-center gap-6 mt-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span className="text-muted-foreground">Total</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-orange-500" />
            <span className="text-muted-foreground">Akan Kedaluwarsa</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
